import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";

// Categories with their column shape
const CATEGORY_SHAPES = {
  laundry: { type: "single", priceLabel: "Price" },
  household: { type: "single", priceLabel: "Price" },
  shoesBags: { type: "single", priceLabel: "Price" },
  mens: { type: "double", priceLabel1: "Dry Clean", priceLabel2: "Steam Iron" },
  womens: { type: "double", priceLabel1: "Dry Clean", priceLabel2: "Steam Iron" },
  woolen: { type: "double", priceLabel1: "Dry Clean", priceLabel2: "Steam Iron" },
};

const CATEGORY_LABELS = {
  laundry: "Laundry",
  mens: "Men's Wear",
  womens: "Women's Wear",
  woolen: "Woolen",
  household: "Household",
  shoesBags: "Shoes & Bags",
};

const CATEGORY_ORDER = ["laundry", "mens", "womens", "woolen", "household", "shoesBags"];

export const PricingEditor = ({ pricing, onChange, onSave, saving }) => {
  const [activeCat, setActiveCat] = useState("laundry");

  const updateCategoryField = (cat, field, value) => {
    onChange({
      ...pricing,
      [cat]: { ...pricing[cat], [field]: value },
    });
  };

  const updateItem = (cat, idx, field, value) => {
    const items = [...(pricing[cat]?.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    onChange({
      ...pricing,
      [cat]: { ...pricing[cat], items },
    });
  };

  const addItem = (cat) => {
    const shape = CATEGORY_SHAPES[cat];
    const newItem = shape.type === "single"
      ? { name: "", price: "" }
      : { name: "", dryClean: "", steamIron: "" };
    const items = [...(pricing[cat]?.items || []), newItem];
    onChange({
      ...pricing,
      [cat]: { ...pricing[cat], items },
    });
  };

  const removeItem = (cat, idx) => {
    const items = (pricing[cat]?.items || []).filter((_, i) => i !== idx);
    onChange({
      ...pricing,
      [cat]: { ...pricing[cat], items },
    });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Pricing</CardTitle>
          <CardDescription>
            Edit prices for every category. Changes are live on the website after you click "Save Pricing".
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs value={activeCat} onValueChange={setActiveCat}>
        <TabsList className="flex flex-wrap h-auto p-1">
          {CATEGORY_ORDER.map((cat) => (
            <TabsTrigger key={cat} value={cat} className="text-xs sm:text-sm" data-testid={`pricing-cat-${cat}`}>
              {CATEGORY_LABELS[cat]}
            </TabsTrigger>
          ))}
        </TabsList>

        {CATEGORY_ORDER.map((cat) => {
          const shape = CATEGORY_SHAPES[cat];
          const data = pricing[cat] || { items: [] };
          return (
            <TabsContent key={cat} value={cat} className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{CATEGORY_LABELS[cat]}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Display Label</label>
                      <Input
                        value={data.label || ""}
                        onChange={(e) => updateCategoryField(cat, "label", e.target.value)}
                        data-testid={`pricing-${cat}-label`}
                      />
                    </div>
                    {cat === "laundry" && (
                      <div>
                        <label className="block text-sm font-medium mb-2">Note (e.g. 5 garments / Kg)</label>
                        <Input
                          value={data.note || ""}
                          onChange={(e) => updateCategoryField(cat, "note", e.target.value)}
                          data-testid={`pricing-${cat}-note`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Items header */}
                  <div className="grid gap-2 items-center font-semibold text-xs uppercase tracking-wide text-slate-500 pt-2 border-t"
                       style={{ gridTemplateColumns: shape.type === "single" ? "1fr 160px 40px" : "1fr 140px 140px 40px" }}>
                    <div>Item Name</div>
                    {shape.type === "single" ? (
                      <div>{shape.priceLabel}</div>
                    ) : (
                      <>
                        <div>{shape.priceLabel1}</div>
                        <div>{shape.priceLabel2}</div>
                      </>
                    )}
                    <div></div>
                  </div>

                  {/* Items */}
                  <div className="space-y-2">
                    {(data.items || []).map((item, idx) => (
                      <div key={idx}
                           className="grid gap-2 items-center"
                           style={{ gridTemplateColumns: shape.type === "single" ? "1fr 160px 40px" : "1fr 140px 140px 40px" }}>
                        <Input
                          value={item.name || ""}
                          placeholder="Item name"
                          onChange={(e) => updateItem(cat, idx, "name", e.target.value)}
                          data-testid={`pricing-${cat}-item-${idx}-name`}
                        />
                        {shape.type === "single" ? (
                          <Input
                            value={item.price || ""}
                            placeholder="₹0"
                            onChange={(e) => updateItem(cat, idx, "price", e.target.value)}
                            data-testid={`pricing-${cat}-item-${idx}-price`}
                          />
                        ) : (
                          <>
                            <Input
                              value={item.dryClean || ""}
                              placeholder="₹0"
                              onChange={(e) => updateItem(cat, idx, "dryClean", e.target.value)}
                              data-testid={`pricing-${cat}-item-${idx}-dryClean`}
                            />
                            <Input
                              value={item.steamIron || ""}
                              placeholder="₹0"
                              onChange={(e) => updateItem(cat, idx, "steamIron", e.target.value)}
                              data-testid={`pricing-${cat}-item-${idx}-steamIron`}
                            />
                          </>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          type="button"
                          onClick={() => removeItem(cat, idx)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          aria-label="Remove item"
                          data-testid={`pricing-${cat}-item-${idx}-remove`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addItem(cat)}
                    className="w-full border-dashed"
                    data-testid={`pricing-${cat}-add-item`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item to {CATEGORY_LABELS[cat]}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      <div className="sticky bottom-4 bg-white border rounded-xl p-4 shadow-lg flex items-center justify-between">
        <p className="text-sm text-slate-500">Save once you've made all your edits.</p>
        <Button onClick={onSave} disabled={saving} data-testid="save-pricing-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Pricing
        </Button>
      </div>
    </div>
  );
};
